import { useAuthStore } from '../store/auth'
import type { ApiError } from '../types'

const BASE_URL = import.meta.env.VITE_ORZFORGE_API_URL?.replace(/\/+$/, '') ?? ''
const PROJECTS_BASE_URL = import.meta.env.VITE_ORZFORGE_PROJECTS_API_URL?.replace(/\/+$/, '') ?? ''

export class OrzForgeApiError extends Error {
  code: string
  status: number

  constructor(code: string, message: string, status: number) {
    super(message)
    this.name = 'OrzForgeApiError'
    this.code = code
    this.status = status
  }
}

function getCsrfToken(): string {
  if (typeof document === 'undefined') return ''
  const match = document.cookie.match(/\borzforge_csrf=([^;]+)/)
  return match?.[1] ?? ''
}

export async function fetchApi(
  path: string,
  options: RequestInit = {},
): Promise<unknown> {
  const url = `${BASE_URL}${path}`
  const headers = new Headers(options.headers)

  if (options.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  const apiKey = useAuthStore.getState().apiKey
  if (apiKey && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${apiKey}`)
  }

  // CSRF protection: the worker requires this non-simple header so cross-site
  // requests trigger a CORS preflight that will be rejected for unknown origins.
  headers.set('X-Requested-With', 'XMLHttpRequest')

  const csrf = getCsrfToken()
  if (csrf) {
    headers.set('X-OrzForge-Csrf', csrf)
  }

  const res = await fetch(url, {
    ...options,
    credentials: 'include',
    headers,
  })

  let body: unknown
  const text = await res.text()
  if (text) {
    try {
      body = JSON.parse(text)
    } catch {
      body = text
    }
  }

  if (!res.ok) {
    const err = body as ApiError | undefined
    throw new OrzForgeApiError(
      err?.error?.code ?? 'unknown',
      err?.error?.message ?? `HTTP ${res.status}`,
      res.status,
    )
  }

  return body
}

export async function fetchProjectsApi(
  apiKey: string,
  path: string,
  options: RequestInit = {},
): Promise<unknown> {
  const url = `${PROJECTS_BASE_URL}${path}`
  const headers = new Headers(options.headers)

  if (options.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  headers.set('Authorization', `Bearer ${apiKey}`)
  headers.set('X-Requested-With', 'XMLHttpRequest')

  const csrf = getCsrfToken()
  if (csrf) {
    headers.set('X-OrzForge-Csrf', csrf)
  }

  const res = await fetch(url, {
    ...options,
    credentials: 'include',
    headers,
  })

  let body: unknown
  const text = await res.text()
  if (text) {
    try {
      body = JSON.parse(text)
    } catch {
      body = text
    }
  }

  if (!res.ok) {
    const err = body as ApiError | undefined
    throw new OrzForgeApiError(
      err?.error?.code ?? 'unknown',
      err?.error?.message ?? `HTTP ${res.status}`,
      res.status,
    )
  }

  return body
}

/**
 * Exchange a raw API key for an HttpOnly session cookie.
 *
 * The raw key is never persisted on the client by this helper; callers are
 * responsible for clearing it when it is no longer needed.
 */
export async function exchangeApiKeyForSession(
  apiKey: string,
): Promise<{ account_id: string }> {
  return (await fetchApi('/v1/auth/session', {
    method: 'POST',
    body: JSON.stringify({ api_key: apiKey }),
  })) as { account_id: string }
}

export async function checkSession(): Promise<{ account_id: string; plan: string }> {
  return (await fetchApi('/v1/auth/session')) as {
    account_id: string
    plan: string
  }
}

export async function logoutFromSession(): Promise<void> {
  await fetchApi('/v1/auth/session', { method: 'DELETE' })
}
